import { Card, CardDescription, CardTitle, FetchingButton } from '@glade-ai/ui';
import { ArrowRight, Clock } from '@phosphor-icons/react';
import DOMPurify from 'isomorphic-dompurify';
import { useRouter } from 'next/router';
import React, { type ReactElement, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { priceWithT } from '@/format';
import { type isSubscriptionProduct } from '@/helpers/helper';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import type HandbookPdfContainer from '@/lib/UserProfile/components/HandbookPdf/HandbookPdfContainer';
import type ProductTypeContainer from '@/lib/UserProfile/components/ProductTypeContainer';
import { type MediaApiModels } from '@/tsClient/servers/noodleMedia';
import type * as ApiModels from '@/typings/api-models';
import { type Asset, type ProductType } from '@/typings/graphql-models';

import type NewServiceCard from '../NewServiceCard';

import s from './ConsultationCard.module.scss';

type Comments = Array<
  Pick<ApiModels.Message, 'id' | 'title' | 'text' | 'isDeleted' | 'createdAt' | 'type'> & {
    parent?:
      | (Pick<ApiModels.Message, 'id'> & {
          owner?: {
            name?: string | null;
            image?: { url: string } | null;
          } | null;
        })
      | null;
    owner?: {
      name?: string | null;
      image?: { url: string } | null;
    } | null;
    tags: {
      id: string;
      name: string;
    }[];
    medias: Pick<MediaApiModels.Media, 'id' | 's3OriginId' | 'vimeoThumbnailUrl' | 'vimeoUri' | 'type'>[];
    children: Array<{
      owner?: {
        id: string;
        name?: string | null;
        image?: { url: string } | null;
      } | null;
    }>;
  }
>;

type ConsultationCardPrice = Pick<ApiModels.Price, 'id' | 'price' | 'isActive' | 'sessionDuration' | 'currency'>;

type ConsultationCardProduct = Pick<ApiModels.Product, 'id' | 'slug' | 'title' | 'includesBroadcasts' | 'isActive'> & {
  comments?: Comments;
  image?: Pick<Asset, 'url'> | null;
  prices?: ConsultationCardPrice[];
  productTypes: Pick<ProductType, 'title' | 'noodleProductType' | 'slug'>[];
  initiateWorkflowCTAs: Array<Pick<ApiModels.InitiateWorkflowCTA, 'id' | 'title' | 'CTA' | 'description'>>;
  messageFromCreator?: { html: string } | null;
} & Parameters<typeof isSubscriptionProduct>[0] &
  Parameters<typeof NewServiceCard>[0]['product'] &
  Parameters<typeof ProductTypeContainer>[0]['bytes'][number] &
  Parameters<typeof HandbookPdfContainer>[0]['bytes'][number];

type ConsultationCardProps = {
  product?: ConsultationCardProduct;
  creatorSlug: string;
  open?: boolean;
};

const ConsultationCard = ({ product, creatorSlug }: ConsultationCardProps): ReactElement => {
  const [isRouting, setIsRouting] = useState(false);
  const { t } = useTranslation();
  const labelId = useId();
  const descriptionId = useId();

  const router = useRouter();

  const sessionDuration = product?.prices?.[0]?.sessionDuration;

  const getTextFromHTML = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(html);
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const productDescription = getTextFromHTML(product?.messageFromCreator?.html ?? '');

  const routeToProduct = async (): Promise<void> => {
    if (!product) return;
    setIsRouting(true);
    await router.push({
      pathname: getUrl(IDENTIFIERS.PRODUCT, { creatorSlug, productSlug: product.slug }),
      query: { fromTab: 'Bookings' },
    });
    setIsRouting(false);
  };

  const showDescription = product?.messageFromCreator?.html && getTextFromHTML(product.messageFromCreator.html).trim();
  const positiveNonZeroPrice = (product?.prices ?? []).find((p) => p.price > 0);

  return (
    <Card aria-labelledby={labelId} aria-describedby={descriptionId} className='gap-0 lg:-mx-4 px-4 mb-4'>
      <CardTitle id={labelId} className={s.consultationCard__title}>
        {product?.title}
      </CardTitle>
      <CardDescription id={descriptionId}>
        {sessionDuration && (
          <p className={s.consultationCard__mainSection__footer__tag}>
            <Clock size={16} />
            <span>{t('durationInMinutes', { count: sessionDuration })}</span>
          </p>
        )}

        {showDescription && (
          <p className={s.consultationCard__description} style={productDescription.length < 400 ? { columns: 1 } : {}}>
            {productDescription}
          </p>
        )}

        <FetchingButton variant='secondary' onClick={routeToProduct} fetching={isRouting}>
          Book for {priceWithT.price(t, positiveNonZeroPrice, true)}
          <ArrowRight size={16} />
        </FetchingButton>
      </CardDescription>
    </Card>
  );
};

export default ConsultationCard;
